// Maps Office.js / runtime failures to the tool-result error shape of PLAN-agent-v2 §10.4 (#15):
// `{error, code, retryable, hint}` — never thrown to the UI, always returned to the agent so the
// graph can self-correct.
import type { ToolError } from '../../types/excel';

/** Office.js error codes we know how to advise on. */
const HINTS: Record<string, { retryable: boolean; hint: string }> = {
  InvalidArgument: { retryable: true, hint: 'Check the sheet name and A1 address; the range or argument was rejected by Excel.' },
  ItemNotFound: { retryable: true, hint: 'The sheet, table, name or chart does not exist. Call excel_get_workbook_overview to list what exists, then retry with an exact name.' },
  ItemAlreadyExists: { retryable: true, hint: 'An item with that name already exists. Pick another name or reuse the existing item.' },
  AccessDenied: { retryable: false, hint: 'The sheet or workbook is protected. Ask the user to unprotect it (or use excel_set_protection if allowed).' },
  InvalidOperation: { retryable: true, hint: 'Excel refused the operation in the current state (e.g. merged cells, table overlap, protected sheet). Inspect the target range first.' },
  InvalidSelection: { retryable: true, hint: 'The selection is invalid; specify an explicit sheet and address.' },
  InvalidReference: { retryable: true, hint: 'The reference is invalid; use a plain A1 address relative to the sheet parameter.' },
  GeneralException: { retryable: true, hint: 'Excel raised a general error. Retry once; if it persists, narrow the operation.' },
  NotImplemented: { retryable: false, hint: 'This Excel build does not support the operation. Tell the user.' },
  ApiNotFound: { retryable: false, hint: 'The Office.js API is unavailable in this Excel version. Tell the user.' },
  Timeout: { retryable: true, hint: 'Excel timed out. Retry with a smaller range.' },
  InsufficientMemory: { retryable: true, hint: 'The operation was too large for Excel. Split it into smaller ranges.' },
  ShapeNotFound: { retryable: true, hint: 'No chart/shape with that name on the sheet; use excel_list_charts.' },
  InvalidOperationInCellEditMode: { retryable: true, hint: 'The user is typing in a cell, and Excel refuses every call until they finish. Ask them to press Enter or Esc, then retry.' },
};

export function isOfficeError(err: unknown): err is OfficeExtension.Error {
  return typeof err === 'object' && err !== null && 'code' in err && 'message' in err && typeof (err as { code: unknown }).code === 'string';
}

/** Excel refuses every API call while the user is typing in a cell (`Excel.ErrorCodes.invalidOperationInCellEditMode`).
 *  The state is transient: the same call succeeds once the user presses Enter or Esc. */
export function isCellEditModeError(err: unknown): boolean {
  return isOfficeError(err) && err.code === 'InvalidOperationInCellEditMode';
}

/** Build the tool-error result for any thrown value. */
export function toToolError(err: unknown, context?: string): ToolError {
  if (isOfficeError(err)) {
    const known = HINTS[err.code];
    const msg = err.message || err.code;
    return {
      error: context ? `${context}: ${msg}` : msg,
      code: err.code,
      retryable: known?.retryable ?? true,
      hint: known?.hint ?? 'Inspect the error, adjust the parameters and retry once. If it recurs, explain it to the user.',
    };
  }
  const message = err instanceof Error ? err.message : String(err);
  return {
    error: context ? `${context}: ${message}` : message,
    code: err instanceof Error && err.name && err.name !== 'Error' ? err.name : 'RuntimeError',
    retryable: true,
    hint: 'Inspect the error, adjust the parameters and retry once. If it recurs, explain it to the user.',
  };
}

/** Error for invalid tool input caught before calling Office.js. */
export function invalidInput(message: string): ToolError {
  return { error: `Invalid input: ${message}`, code: 'InvalidInput', retryable: true, hint: 'Fix the parameters and call the tool again.' };
}

/** Error for results that exceed the size caps (#10). */
export function tooLarge(cells: number, maxCells: number): ToolError {
  return {
    error: `Result too large (${cells} cells, max ${maxCells})`,
    code: 'ResultTooLarge',
    retryable: true,
    hint: 'Narrow the range or set max_cells (hard maximum 10000). Prefer reading headers first, then only the columns you need.',
  };
}

export function isToolError(result: unknown): result is ToolError {
  return typeof result === 'object' && result !== null && typeof (result as ToolError).error === 'string';
}
